/**
 * The trader's script list, fetched once for the whole screen.
 *
 * A context rather than a hook each pane calls for itself. Two chart panes and
 * the studies panel all need the same list, and three independent fetches
 * would give three answers that drift the moment one of them saves — the panel
 * would show a script switched on that the charts had not heard about.
 *
 * The RUNNING is not shared: a study is a function of the bars in front of it,
 * and two panes on different symbols are two different runs. See `studies.ts`.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import type { ScriptsResponse } from '@aegis/api-client';
import { api } from './api.js';

/** How one script's last run went, on one chart. */
export interface ScriptRunState {
  readonly state: 'running' | 'ok' | 'error' | 'timeout' | 'disabled' | 'refused';
  readonly message?: string;
  readonly gaps: number;
}

export interface ScriptsState {
  readonly scripts: ScriptsResponse | null;
  readonly loading: boolean;
  /** Re-fetch after a save, an install, a settings change or an uninstall. */
  reload(): void;
  /**
   * How each script is faring, worst answer across every chart drawing it.
   *
   * Reported by the panes rather than computed by the panel. The panel has no
   * bars of its own, and giving it some so it could run the scripts itself
   * would spin a second Worker per script to answer a question the charts have
   * already answered.
   */
  readonly runStates: Readonly<Record<string, ScriptRunState>>;
  /** Called by a chart pane after a run. `paneId` scopes what it overwrites. */
  report(paneId: string, states: Readonly<Record<string, ScriptRunState>>): void;
}

const EMPTY: ScriptsState = {
  scripts: null, loading: false, reload: () => {}, runStates: {}, report: () => {},
};

/**
 * Which state wins when two charts disagree about the same script.
 *
 * Worst first. A study that works on ES and throws on 6E is a broken study,
 * and reporting the working chart's answer would leave the trader looking at a
 * blank pane with nothing to read.
 */
const SEVERITY: Readonly<Record<ScriptRunState['state'], number>> = {
  refused: 5, disabled: 4, timeout: 3, error: 2, running: 1, ok: 0,
};

const Ctx = createContext<ScriptsState>(EMPTY);

export function ScriptsProvider(
  { children, onError }: { children: ReactNode; onError?: (m: string) => void },
): JSX.Element {
  const [scripts, setScripts] = useState<ScriptsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [byPane, setByPane] = useState<Record<string, Record<string, ScriptRunState>>>({});

  const reload = useCallback((): void => {
    setLoading(true);
    void api.scripts()
      .then(setScripts)
      .catch((e: unknown) => {
        /* A failed list is not a blank list. Leaving the previous one in place
           keeps the studies on screen drawing rather than making a network
           blip look like every script being uninstalled. */
        onError?.(e instanceof Error ? e.message : 'Could not load your scripts.');
      })
      .finally(() => setLoading(false));
  }, [onError]);

  useEffect(() => { reload(); }, [reload]);

  const report = useCallback(
    (paneId: string, states: Readonly<Record<string, ScriptRunState>>): void => {
      setByPane((prev) => {
        /* Bail out when nothing changed. A pane reports after every run — twice
           a second on a ticking chart — and setting state unconditionally would
           re-render the whole screen at that rate. */
        const before = prev[paneId];
        if (before && sameStates(before, states)) return prev;
        return { ...prev, [paneId]: { ...states } };
      });
    },
    [],
  );

  const runStates = useMemo(() => {
    const worst: Record<string, ScriptRunState> = {};
    for (const states of Object.values(byPane)) {
      for (const [scriptId, state] of Object.entries(states)) {
        const held = worst[scriptId];
        if (!held || SEVERITY[state.state] > SEVERITY[held.state]) worst[scriptId] = state;
      }
    }
    return worst;
  }, [byPane]);

  const value = useMemo<ScriptsState>(
    () => ({ scripts, loading, reload, runStates, report }),
    [scripts, loading, reload, runStates, report],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The shared list.
 *
 * Outside a provider this returns an empty, permanently-idle state rather than
 * throwing. A chart rendered in a pane window has no provider above it, and a
 * chart that crashed because nobody had asked for scripts would be a worse
 * outcome than one that draws no studies.
 */
export function useScripts(): ScriptsState {
  return useContext(Ctx);
}

function sameStates(
  a: Readonly<Record<string, ScriptRunState>>,
  b: Readonly<Record<string, ScriptRunState>>,
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => a[k]?.state === b[k]?.state
    && a[k]?.message === b[k]?.message
    && a[k]?.gaps === b[k]?.gaps);
}
